import { DEFAULT_PRIORITY_ORDER, TASK_PRIORITY } from './appConstants';

/**
 * Returns the case-insensitive index of a priority in DEFAULT_PRIORITY_ORDER.
 * Custom priorities return Number.MAX_SAFE_INTEGER so they sort after known priorities.
 *
 * @param {string|Object} priority
 * @returns {number}
 */
export const getPriorityOrderIndex = (priority) => {
  const name = (typeof priority === 'string' ? priority : priority?.name || '').trim().toLowerCase();
  if (!name) return Number.MAX_SAFE_INTEGER;
  const idx = DEFAULT_PRIORITY_ORDER.findIndex(orderItem => orderItem.toLowerCase() === name);
  return idx === -1 ? Number.MAX_SAFE_INTEGER : idx;
};

/**
 * Sorts priority items deterministically and case-insensitively.
 * Follows DEFAULT_PRIORITY_ORDER ('Critical', 'High', 'Medium', 'Low', 'Unassigned'),
 * then sorts custom priorities alphabetically by name.
 *
 * @param {Array} priorities
 * @returns {Array}
 */
export const sortPriorities = (priorities) => {
  if (!Array.isArray(priorities)) return [];
  return [...priorities].sort((a, b) => {
    const indexA = getPriorityOrderIndex(a);
    const indexB = getPriorityOrderIndex(b);

    if (indexA !== indexB) {
      return indexA - indexB;
    }

    const nameA = ((typeof a === 'string' ? a : a?.name) || '').trim();
    const nameB = ((typeof b === 'string' ? b : b?.name) || '').trim();
    return nameA.localeCompare(nameB);
  });
};

/**
 * Finds the Unassigned priority item from the priorities list.
 * Matches case-insensitively against TASK_PRIORITY.UNASSIGNED ('unassigned').
 *
 * @param {Array} priorities
 * @returns {Object|null}
 */
export const getUnassignedPriority = (priorities = []) => {
  if (!Array.isArray(priorities) || priorities.length === 0) return null;
  const target = TASK_PRIORITY.UNASSIGNED.toLowerCase();
  return priorities.find(p => (p?.name || '').trim().toLowerCase() === target) || null;
};

/**
 * Returns the ID of the Unassigned priority item, or an empty string if none exists.
 * CRUCIAL: Does NOT fall back to priorities[0] (which would be 'Critical' due to priority sorting).
 *
 * @param {Array} priorities
 * @returns {string|number|''}
 */
export const getDefaultPriorityId = (priorities = []) => {
  const unassigned = getUnassignedPriority(priorities);
  return unassigned?.id ?? '';
};

/**
 * Returns the name of the default priority ('Unassigned').
 *
 * @param {Array} priorities
 * @returns {string}
 */
export const getDefaultPriorityName = (priorities = []) => {
  const unassigned = getUnassignedPriority(priorities);
  return unassigned?.name || TASK_PRIORITY.UNASSIGNED;
};
